import { useApi } from '@/@diffrag/hooks/useApi'
import { ContentView } from '@/@diffrag/layout/ContentView'
import { SimpleTable } from '@/@diffrag/table/SimpleTable'
import { api } from '@/api'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  IAdminBannerSaveLanguageEnum,
  IBannerRO,
  IBannerRoLanguageEnum,
} from 'api'
import { ChevronDown } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const languageMap: Record<IBannerRoLanguageEnum, string> = {
  [IBannerRoLanguageEnum.Ko]: '한국어',
  [IBannerRoLanguageEnum.ZhCN]: '중국어(간체)',
  [IBannerRoLanguageEnum.ZhTW]: '중국어(번체)',
  [IBannerRoLanguageEnum.Ja]: '일본어',
  [IBannerRoLanguageEnum.En]: '영어',
  [IBannerRoLanguageEnum.Th]: '태국어',
}

const languageOptions = [
  { label: '전체', value: undefined as undefined },
  { label: '한국어', value: IBannerRoLanguageEnum.Ko },
  { label: '중국어(간체)', value: IBannerRoLanguageEnum.ZhCN },
  { label: '중국어(번체)', value: IBannerRoLanguageEnum.ZhTW },
  { label: '일본어', value: IBannerRoLanguageEnum.Ja },
  { label: '영어', value: IBannerRoLanguageEnum.En },
  { label: '태국어', value: IBannerRoLanguageEnum.Th },
]

export default function Banner() {
  const navigate = useNavigate()
  const [selectedLanguage, setSelectedLanguage] = useState<
    IBannerRoLanguageEnum | undefined
  >(undefined)

  const { data, isFetching, refetch } = useApi({
    id: 'bannerList',
    fn: api().adminBanner.adminBannerList,
  })

  const filteredData = useMemo(() => {
    if (!data?.items) return { items: [], total: 0 }
    if (selectedLanguage === undefined) return data

    const filtered = data.items.filter(
      (item) => item.language === selectedLanguage,
    )
    return {
      items: filtered,
      total: filtered.length,
    }
  }, [data, selectedLanguage])

  const selectedLanguageLabel =
    selectedLanguage === undefined
      ? '전체'
      : languageMap[selectedLanguage] || selectedLanguage

  const onDelete = async (rows: IBannerRO[]) => {
    try {
      await Promise.all(
        rows.map((row) => api().adminBanner.adminBannerDelete(row.id)),
      )
      refetch()
    } catch (error) {
      console.error(error)
      alert('삭제 중 오류가 발생했습니다.')
    }
  }

  const onReorder = async (
    row: IBannerRO,
    direction: 'UP' | 'DOWN' | 'TOP' | 'BOTTOM',
  ) => {
    console.log(row, direction)
    try {
      await api().adminBanner.adminBannerUpdate(row.id, {
        order: row.order,
        imageId: row.image.id,
        link: row.link || undefined,
        language: row.language as unknown as IAdminBannerSaveLanguageEnum,
      })
      refetch()
    } catch (error) {
      console.error(error)
    }
  }

  return (
    <ContentView>
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">언어 필터:</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                className="min-w-[150px] justify-between"
              >
                {selectedLanguageLabel}
                <ChevronDown className="ml-2 size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="z-50 w-[150px] border bg-white shadow-lg"
            >
              {languageOptions.map((option) => (
                <DropdownMenuCheckboxItem
                  key={option.value || 'all'}
                  checked={selectedLanguage === option.value}
                  onCheckedChange={() => setSelectedLanguage(option.value)}
                >
                  {option.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <SimpleTable
        createHref="/banner/edit"
        totalCount={filteredData.total || 0}
        onDelete={onDelete}
        onRowClick={(row) => {
          navigate(`/banner/edit?id=${row.id}`)
        }}
        columns={[
          {
            label: 'ID',
            key: 'id',
            width: 100,
          },
          {
            label: '언어',
            key: 'language',
            width: 100,
            formatter: (value) => {
              return languageMap[value as IBannerRoLanguageEnum] || value
            },
          },
          {
            label: '순서',
            key: 'order',
            width: 100,
          },
          {
            label: '이미지',
            key: 'image.path',
            width: 200,
            formatter: (value) => {
              if (!value) return '-'
              return (
                <img
                  src={value}
                  alt="banner"
                  className="h-8 w-24 object-cover"
                />
              )
            },
          },
          {
            label: '링크',
            key: 'link',
            width: 300,
          },
        ]}
        data={filteredData.items || []}
        isLoading={isFetching}
        options={{
          disableSort: true,
        }}
      />
    </ContentView>
  )
}
